import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Heart, MessageCircle, MessagesSquare, Banknote, CalendarDays, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = {
  like: Heart, comment: MessageCircle, message: MessagesSquare,
  payout: Banknote, event: CalendarDays,
};
const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export default function NotificationBell({ align = "right" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications").then((r) => r.data),
    refetchInterval: 30000,
  });
  const items = data?.notifications || [];
  const unread = data?.unread || 0;

  // Tap / click to toggle; tapping outside closes. Works on touch + desktop.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("touchstart", onDoc); };
  }, [open]);

  const markAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
    const isMsg = ["message", "inquiry"].includes(n.type);
    if (isMsg) {
      // Use thread_id (pair_key) to find the conversation partner's ID, then navigate to Messages page.
      const tid = n.thread_id || "";
      if (tid && tid !== "__unresolvable__") {
        const parts = tid.split(":");
        const otherId = parts.find((p) => p && p !== (user?.id || ""));
        if (otherId) { nav(`/messages?to=${otherId}`); return; }
      }
      // Fallback: extract sender_id from the stored link (/messages?to=<id>)
      if (n.sender_id && n.sender_id !== (user?.id || "")) { nav(`/messages?to=${n.sender_id}`); return; }
      const m = /^\/messages\?to=([A-Za-z0-9]+)/.exec(n.link || "");
      if (m && m[1] !== (user?.id || "")) { nav(`/messages?to=${m[1]}`); return; }
      // Guest inquiry (no sender account): use the stored dashboard link (Inquiries inbox)
      if (n.type === "inquiry" && n.link && n.link !== "/messages") { nav(n.link); return; }
      // Unresolvable — open inbox; never open a blank compose panel
      nav("/messages"); return;
    }
    if (n.link) nav(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="nav-bell-btn" onClick={() => setOpen((v) => !v)} aria-label="Notifications" className="relative text-white hover:text-[#F0C9B8] transition-colors p-1">
        <Bell size={20} />
        {unread > 0 && <span data-testid="nav-bell-badge" className="absolute -top-1 -right-1 grid place-items-center h-4 min-w-[16px] px-1 rounded-full bg-[#D98E73] text-white text-[10px] font-semibold">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className={`absolute top-full mt-2 w-[calc(100vw-2rem)] max-w-80 z-50 ${align === "left" ? "left-0" : "right-0"}`} data-testid="nav-bell-menu">
          <div className="bg-white rounded-2xl shadow-xl border border-[#E5E0D8] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E0D8]">
              <span className="font-semibold text-[#1A242B] text-sm">Notifications</span>
              {unread > 0 && <button onClick={markAll} data-testid="nav-bell-readall" className="text-xs text-[#144F75] hover:underline flex items-center gap-1"><CheckCheck size={13} /> Mark all read</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-[#8FA89B]">You're all caught up.</p>
              ) : items.map((n) => {
                const Ic = ICONS[n.type] || Bell;
                return (
                  <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-${n.id}`}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-[#E5E0D8]/60 hover:bg-[#144F75]/[0.03] ${n.read ? "" : "bg-[#144F75]/[0.04]"}`}>
                    <span className="mt-0.5 h-8 w-8 rounded-full bg-[#144F75]/10 text-[#144F75] grid place-items-center shrink-0"><Ic size={15} /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#1A242B] leading-snug">{n.text}</p>
                      <span className="text-[11px] text-[#8FA89B]">{timeAgo(n.created_at)}</span>
                    </div>
                    {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#D98E73] shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
